import _ from 'lodash';

/**
 * Formats a number with common suffixes (k, m, b, t, etc.) for large values.
 *
 * @param num - The number to format.
 * @param decimals - The number of decimal places to retain (default is 1).
 * @returns A string with the formatted number.
 */
export const getNumberWithTierSuffix = (num: number, decimals = 1): string => {
  if (_.isNil(num) || num === 0) return '0';

  const suffixes = ['', 'k', 'm', 'b', 't', 'q']; // "", thousand, million, billion, trillion, quadrillion
  const tier = Math.floor(Math.log10(Math.abs(num)) / 3); // Determine the tier (thousands, millions, etc.)

  if (tier === 0) {
    return num.toString(); // Return the number as-is if it's less than 1,000
  }

  const suffix = suffixes[tier] || `e${tier * 3}`; // Fallback to scientific notation for very large tiers
  const scaled = num / Math.pow(10, tier * 3); // Scale the number down by the appropriate factor
  return scaled.toFixed(decimals) + suffix;
};

/**
 * Formats a number with spaces between thousands.
 *
 * @param num - The number to format.
 * @returns A string with the formatted number.
 */
export const formatNumberWithSpaces = (num: number): string => {
  if (_.isNil(num)) {
    return '0';
  }
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

/**
 * Formats a number as a USD currency string with thousands separators and no decimals.
 * Example: 163887 → "$163,887"
 */
export const formatCurrency = (value: number): string => {
  const rounded = Math.round(value);
  return '$' + rounded.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/**
 * Generates a unique ID that does not exist in the provided array of existing IDs.
 * If a maximum value is provided, the ID will be generated within the range of 1 to that maximum value.
 * @param existingIds
 * @param max
 */
export const generateUniqueId = (existingIds: number[], max?: number): number => {
  let newId;
  do {
    newId = _.random(1, max ? max : 1000000);
  } while (existingIds.includes(newId));
  return newId;
};

/** * Encodes a string to Base64 format.
 * @param str - The string to decode.
 */
export const decodeBase64 = (str: string): string => {
  //Check if string is empty
  if (!str) {
    return '';
  }

  try {
    return Buffer.from(str, 'base64').toString();
  } catch (error) {
    console.error('Error decoding Base64 string:', error);
    return '';
  }
};

/**
 * Encodes a string to Base64 format.
 * @param str - The string to encode.
 */
export const encodeBase64 = (str: string): string => {
  if (!str) {
    return '';
  }

  try {
    return Buffer.from(str).toString('base64');
  } catch (error) {
    console.error('Error encoding Base64 string:', error);
    return '';
  }
};

export type StringType = 'TEXT' | 'HTML' | 'JSON';

/**
 * Determines the type of a given string: JSON, TEXT, or HTML.
 * @param {string} str - The string to analyze.
 * @returns {string} - The type of the string: 'JSON', 'HTML', or 'TEXT'.
 */
export const determineStringType = (str: string): StringType => {
  if (!str || typeof str !== 'string') {
    return 'TEXT'; // Default to TEXT for empty or non-string values
  }

  try {
    const trimmed = str.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      JSON.parse(trimmed);
      return 'JSON'; // Only treat objects/arrays as JSON
    }
  } catch (e) {
    // fall through
  }
  if (/<[a-z][\s\S]*>/i.test(str)) {
    return 'HTML'; // If it contains HTML tags, it's HTML
  }
  return 'TEXT'; // Otherwise, it's plain text
};
